// Building-scope Konfiguraatio view — Signaalit (declare + mapping/coverage home).
//
// Config home for which signals are in use. Pairing: Signaalien kytkennät
// (source → KB). Declare on/off: Signaalien aktivointi. Mapping & coverage chips:
// kytketty / käytössä / ehdotus. Live values belong to Datan laatu.
// Zones/building groups stay read-only. "Laitteet ilman tuotua dataa" remains the
// first-CSV entry into equipment import (#907 / story #1140).
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plug } from "lucide-react";
import { toast } from "sonner";
import {
  acceptContextSetpointSuggestion,
  listBuildingServices,
  getSimulatedFeedStatus,
  listConnectorSignalSchemas,
  listCsvSignalSchemas,
  listDataPointSources,
  listEquipment,
  listEquipmentContextSetpoints,
  setManualContextSetpointValue,
  type BuildingService,
  type ConnectorSignalSchemaOut,
  type ContextSetpoint,
  type CsvSignalSchemaOut,
  type DataPointSource,
  type Equipment,
} from "@/lib/api/building";
import {
  getEquipmentKBBehaviorsSummary,
  getKBDataPointLabels,
  type EquipmentBehaviorSummary,
} from "@/lib/api/agent";
import {
  SignaalitList,
  type SignalSourceCoverage,
} from "@/components/building/datapoints/SignaalitList";
import {
  SignaalitMuokkaaDialog,
  type MuokkaaSignalRow,
} from "@/components/building/datapoints/SignaalitMuokkaaDialog";
import { HallitseSignaalejaDialog } from "@/components/building/datapoints/HallitseSignaalejaDialog";
import {
  build_muokkaa_rows,
  resolve_source_signal_by_source,
  read_preferred_source_targets,
  write_preferred_source_targets,
} from "@/components/building/datapoints/signaalit-muokkaa-choice";
import { DataPointMappingPanel } from "@/components/building/equipment/DataPointMappingPanel";
import { labelsFromSources } from "@/lib/data-point-labels";
import {
  equipmentTypeIcon,
  equipmentTypeNameFi,
  useEquipmentTypeMeta,
  useKbSensorTypes,
} from "@/lib/equipment-type-meta";
import { SectionPanel } from "@/components/ui/section-panel";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

export function ConfigSignalMappingSection({
  buildingId,
  operator = false,
  refreshKey,
  onChanged,
}: {
  buildingId: string;
  operator?: boolean;
  refreshKey?: number;
  onChanged?: () => void;
}) {
  const eqMeta = useEquipmentTypeMeta();
  const sensors = useKbSensorTypes();
  // Family + minted-sibling name_fi (same source Kyvykkyydet/CapabilityList uses) — history
  // label_fi rarely covers a sibling id (only the minted id is stored, not its family), so
  // without this the Signaali column falls through to the raw de-underscored English id.
  const { data: kbLabels } = useQuery({
    queryKey: ["kb-data-point-labels"],
    queryFn: getKBDataPointLabels,
    staleTime: Infinity,
  });
  const [sources, setSources] = useState<DataPointSource[]>([]);
  const [summary, setSummary] = useState<EquipmentBehaviorSummary[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [csvSchemas, setCsvSchemas] = useState<CsvSignalSchemaOut[]>([]);
  const [connectorSchemas, setConnectorSchemas] = useState<
    ConnectorSignalSchemaOut[]
  >([]);
  const [buildingServices, setBuildingServices] = useState<BuildingService[]>(
    [],
  );
  const [contextSetpointsBySource, setContextSetpointsBySource] = useState<
    Record<string, ContextSetpoint[]>
  >({});
  /** equipment_id → Device unmapped CSV headers (ei kytketty tag). */
  const [unmappedBySource, setUnmappedBySource] = useState<
    Record<string, string[]>
  >({});
  /** equipment_id → lähdesignaali→Signaali kept when switch is off. */
  const [preferredByEquipment, setPreferredByEquipment] = useState<
    Record<string, Record<string, string>>
  >({});
  const [mappingEquipmentId, setMappingEquipmentId] = useState<string | null>(
    null,
  );
  const [kartoitusEquipmentId, setKartoitusEquipmentId] = useState<
    string | null
  >(null);
  const [hallitseEquipmentId, setHallitseEquipmentId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listDataPointSources(buildingId).catch((): DataPointSource[] => []),
      getEquipmentKBBehaviorsSummary(buildingId).catch(
        (): EquipmentBehaviorSummary[] => [],
      ),
      listEquipment(buildingId).catch((): Equipment[] => []),
      listCsvSignalSchemas(buildingId).catch((): CsvSignalSchemaOut[] => []),
      listConnectorSignalSchemas(buildingId).catch(
        (): ConnectorSignalSchemaOut[] => [],
      ),
      listBuildingServices(buildingId).catch((): BuildingService[] => []),
    ]).then(async ([srcs, sum, eq, schemas, connectorMaps, services]) => {
      if (cancelled) return;
      // One context-setpoint (asetusarvo) family read per equipment — no bulk endpoint exists
      // yet, mirroring listEquipmentContextSetpoints' per-equipment shape (story #1515).
      const contextSetpointEntries = await Promise.all(
        eq.map((e): Promise<[string, ContextSetpoint[]]> =>
          listEquipmentContextSetpoints(buildingId, e.id)
            .then((cs): [string, ContextSetpoint[]] => [e.id, cs])
            .catch((): [string, ContextSetpoint[]] => [e.id, []]),
        ),
      );
      // Sim connectors only — CSV-only maps have no Device unmapped_signals list.
      const connectorEquipmentIds = new Set(
        connectorMaps.map((c) => c.equipment_id),
      );
      const unmappedEntries = await Promise.all(
        eq
          .filter((e) => connectorEquipmentIds.has(e.id))
          .map((e): Promise<[string, string[]]> =>
            getSimulatedFeedStatus(buildingId, e.id)
              .then(
                (status): [string, string[]] => [
                  e.id,
                  status?.unmapped_signals ?? [],
                ],
              )
              .catch((): [string, string[]] => [e.id, []]),
          ),
      );
      if (cancelled) return;
      setSources(srcs);
      setSummary(sum);
      setEquipment(eq);
      setCsvSchemas(schemas);
      setConnectorSchemas(connectorMaps);
      setBuildingServices(services);
      setContextSetpointsBySource(
        Object.fromEntries(
          contextSetpointEntries.filter(([, cs]) => cs.length > 0),
        ),
      );
      setUnmappedBySource(
        Object.fromEntries(
          unmappedEntries.filter(([, names]) => names.length > 0),
        ),
      );
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [buildingId, refreshKey, reloadToken]);

  const mappingEquipment = equipment.find((e) => e.id === mappingEquipmentId);
  const kartoitusEquipment = equipment.find(
    (e) => e.id === kartoitusEquipmentId,
  );
  const hallitseEquipment = equipment.find((e) => e.id === hallitseEquipmentId);

  const unmappedEquipment = useMemo(() => {
    const withSignals = new Set(
      sources
        .filter(
          (s) => s.source_type === "equipment" && s.available_names.length > 0,
        )
        .map((s) => s.source_id),
    );
    return equipment.filter((e) => !withSignals.has(e.id));
  }, [equipment, sources]);

  // History label_fi (real per-source data) wins where it exists; the KB map fills the rest,
  // notably the family/sibling ids history usually never carries.
  const labels = useMemo(
    () => ({ ...kbLabels, ...labelsFromSources(sources) }),
    [sources, kbLabels],
  );

  const coverageBySource = useMemo(() => {
    const map: Record<string, SignalSourceCoverage> = {};
    for (const s of summary) {
      map[s.equipment_id] = {
        hasKb: s.has_kb,
        activeCount: s.active_count,
        unwiredCount: s.unwired_count,
      };
    }
    return map;
  }, [summary]);

  const declaredBySource = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const e of equipment) {
      map[e.id] = e.declared_data_points ?? [];
    }
    return map;
  }, [equipment]);

  /** source_id → canonical id → raw Lähdesignaali, resolved the same way the
   *  signal-map write path resolves which store it would rewrite (#1519
   *  changes-requested) — connectorSchemas (a live Device connector's map)
   *  wins over csvSchemas per equipment_id — see resolve_source_signal_by_source. */
  const sourceSignalBySource = useMemo(
    () => resolve_source_signal_by_source(csvSchemas, connectorSchemas),
    [csvSchemas, connectorSchemas],
  );

  // Union of observed history and the taught map (#1519 changes-requested
  // round 2) — a taught pair with no history row yet (never-sampled or
  // stopped connector feed) must still render, or an unedited Tallenna
  // whole-map replace silently drops it. See build_muokkaa_rows.
  const kartoitusSignals: MuokkaaSignalRow[] = useMemo(() => {
    if (!kartoitusEquipment) return [];
    const src = sources.find(
      (s) =>
        s.source_type === "equipment" && s.source_id === kartoitusEquipment.id,
    );
    const sourceSignals = sourceSignalBySource[kartoitusEquipment.id] ?? {};
    return build_muokkaa_rows(src?.available_names ?? [], sourceSignals);
  }, [kartoitusEquipment, sources, sourceSignalBySource]);

  function refresh() {
    setReloadToken((n) => n + 1);
    onChanged?.();
  }

  // Both context-setpoint (asetusarvo) writes below just re-fetch (refresh()) rather than
  // patching context_setpoints_by_source locally — the row's effective value/source always
  // comes back from a real GET, so Konfiguraatio can never show a different answer than Datan
  // laatu after a write (story #1515/#1529).
  async function handleAcceptContextSetpoint(
    equipmentId: string,
    contextSetpointId: string,
  ) {
    try {
      await acceptContextSetpointSuggestion(
        buildingId,
        equipmentId,
        contextSetpointId,
      );
      toast.success("Asetusarvoehdotus hyväksytty");
      refresh();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Hyväksyntä epäonnistui",
      );
    }
  }

  async function handleSetContextSetpointValue(
    equipmentId: string,
    contextSetpointId: string,
    value: number,
    unit: string | null,
  ) {
    try {
      await setManualContextSetpointValue(
        buildingId,
        equipmentId,
        contextSetpointId,
        value,
        unit,
      );
      toast.success("Asetusarvo tallennettu");
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Tallennus epäonnistui");
    }
  }

  if (loading) {
    return (
      <div
        className={cn(
          cardBase,
          "p-6 text-sm text-muted-foreground animate-pulse",
        )}
      >
        Ladataan signaaleja…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="px-1">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          Konfiguraatio
        </div>
        <h2 className="text-lg font-semibold tracking-tight">Signaalit</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Mitkä signaalit ovat käytössä ja mistä niiden identiteetti tulee — ei
          eläviä arvoja (ne näkyvät Datan laadussa). Signaalien kytkennät
          tallentaa lähde→kohde-parin; Signaalien aktivointi merkitsee mitkä
          signaalit ovat käytössä.
        </p>
      </div>
      <SectionPanel
        icon={Plug}
        title="Datalähteet ja signaalit"
        subtitle="Lähdesignaali · syöte / asetusarvo — kytkennät ja aktivointi laitoryhmän otsikossa tilamerkeillä"
      >
        <SignaalitList
          buildingId={buildingId}
          sources={sources}
          samples={[]}
          behavior_labels={labels}
          mode="config"
          coverage_by_source={coverageBySource}
          declared_by_source={declaredBySource}
          source_signal_by_source={sourceSignalBySource}
          unmapped_lahdesignaalit_by_source={unmappedBySource}
          building_services={buildingServices}
          context_setpoints_by_source={contextSetpointsBySource}
          onKytkennat={
            operator ? (id) => setKartoitusEquipmentId(id) : undefined
          }
          onAktivointi={
            operator ? (id) => setHallitseEquipmentId(id) : undefined
          }
          onAcceptContextSetpoint={
            operator ? handleAcceptContextSetpoint : undefined
          }
          onSetContextSetpointValue={
            operator ? handleSetContextSetpointValue : undefined
          }
        />
      </SectionPanel>
      <p className="text-[11px] text-muted-foreground px-1">
        Signaalinimet ovat vakioituja (KB). CSV-kartta opetetaan Syötä dataa
        -tuonnissa. Signaalien kytkennät vaihtaa pareja; Signaalien aktivointi
        ottaa signaalit käyttöön tai pois.
      </p>

      {operator && unmappedEquipment.length > 0 && (
        <SectionPanel
          icon={Plug}
          title="Laitteet ilman tuotua dataa"
          subtitle="Ei vielä signaaleja — tuo laitekohtainen CSV Syötä dataa -polulla"
        >
          <div className="divide-y divide-border/20">
            {unmappedEquipment.map((e) => (
              <div
                key={e.id}
                className="flex items-center justify-between gap-3 px-1 py-2"
              >
                <div className="min-w-0">
                  <span className="text-sm font-medium">
                    {equipmentTypeIcon(eqMeta, e.equipment_type, sensors)}{" "}
                    {e.name}
                  </span>
                  {e.equipment_tag && (
                    <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                      {e.equipment_tag}
                    </span>
                  )}
                  <span className="ml-2 text-xs text-muted-foreground">
                    {equipmentTypeNameFi(eqMeta, e.equipment_type, sensors)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setMappingEquipmentId(e.id)}
                  className="shrink-0 rounded bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary hover:bg-primary/20"
                >
                  Tuo CSV →
                </button>
              </div>
            ))}
          </div>
        </SectionPanel>
      )}

      {operator && mappingEquipment && (
        <DataPointMappingPanel
          buildingId={buildingId}
          equipmentId={mappingEquipment.id}
          operator={operator}
          initial_declared={mappingEquipment.declared_data_points}
          on_declared_changed={() => {}}
          on_close={() => {
            setMappingEquipmentId(null);
            refresh();
          }}
        />
      )}

      {operator && kartoitusEquipment && (
        <SignaalitMuokkaaDialog
          open={kartoitusEquipmentId != null}
          onOpenChange={(open) => {
            if (!open) setKartoitusEquipmentId(null);
          }}
          buildingId={buildingId}
          equipmentId={kartoitusEquipment.id}
          equipmentName={kartoitusEquipment.name}
          signals={kartoitusSignals}
          labels={labels}
          preferredSourceTargets={{
            ...read_preferred_source_targets(kartoitusEquipment.id),
            ...(preferredByEquipment[kartoitusEquipment.id] ?? {}),
          }}
          onSaved={(preferred) => {
            write_preferred_source_targets(kartoitusEquipment.id, preferred);
            setPreferredByEquipment((prev) => ({
              ...prev,
              [kartoitusEquipment.id]: preferred,
            }));
            refresh();
          }}
        />
      )}

      {operator && hallitseEquipment && (
        <HallitseSignaalejaDialog
          open={hallitseEquipmentId != null}
          onOpenChange={(open) => {
            if (!open) setHallitseEquipmentId(null);
          }}
          buildingId={buildingId}
          equipmentId={hallitseEquipment.id}
          equipmentName={hallitseEquipment.name}
          declaredDataPoints={hallitseEquipment.declared_data_points ?? []}
          observedDataPoints={
            sources
              .find(
                (s) =>
                  s.source_type === "equipment" &&
                  s.source_id === hallitseEquipment.id,
              )
              ?.available_names.map((n) => n.name) ?? []
          }
          sourceSignals={sourceSignalBySource[hallitseEquipment.id] ?? {}}
          labels={labels}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
